/**
 * Base API error — thrown when the backend returns a non-2xx response
 * that we recognize. Carries the backend's structured error code and
 * request_id for support escalation.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Authentication failed', requestId?: string) {
    super(401, 'UNAUTHORIZED', message, requestId);
    this.name = 'UnauthorizedError';
  }
}

export class RateLimitError extends ApiError {
  readonly retryAfterSeconds?: number;

  constructor(message = 'Rate limit exceeded', retryAfterSeconds?: number, requestId?: string) {
    super(429, 'RATE_LIMITED', message, requestId);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class ValidationError extends ApiError {
  constructor(message = 'Request validation failed', requestId?: string) {
    super(422, 'INVALID_REQUEST', message, requestId);
    this.name = 'ValidationError';
  }
}

/**
 * Network failures (fetch threw, offline, DNS, CORS, AbortError unwrapped).
 * Not an ApiError because there's no HTTP status to map.
 */
export class NetworkError extends Error {
  readonly cause?: Error;

  constructor(message: string, cause?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * The client's own timeout fired — distinct from the caller aborting
 * (e.g. the user pressing Stop). Extends NetworkError so classify() falls
 * straight to DEFAULT_ACTION: a retriable failed bubble, exactly what
 * error-policy.ts's table already prescribes for "网络故障、超时".
 */
export class TimeoutError extends NetworkError {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs} ms`);
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

interface BackendErrorBody {
  error?: {
    code?: string;
    message?: string;
    request_id?: string;
  };
}

/**
 * Map an HTTP status + parsed body + Retry-After header to a typed error.
 * Used by the API client when a non-2xx response is received.
 *
 * requestIdHeader is the X-Request-Id response header, used whenever the
 * body doesn't carry the structured error envelope (e.g. a bare 404 with
 * no `error.request_id`) — the failures hardest to diagnose are exactly
 * the ones where losing the correlation id hurts most.
 */
export function mapHttpStatusToError(
  status: number,
  body: unknown,
  retryAfterHeader: string,
  requestIdHeader?: string,
): ApiError {
  const parsed = (body ?? {}) as BackendErrorBody;
  const code = parsed.error?.code ?? `HTTP_${status}`;
  const message = parsed.error?.message ?? `Request failed with status ${status}`;
  const requestId = parsed.error?.request_id ?? requestIdHeader ?? undefined;

  if (status === 401) return new UnauthorizedError(message, requestId);
  if (status === 422) return new ValidationError(message, requestId);
  if (status === 429) {
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
    return new RateLimitError(message, isNaN(retryAfter) ? undefined : retryAfter, requestId);
  }

  return new ApiError(status, code, message, requestId);
}
